import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  X,
  Play,
  Film,
  Sparkles,
  Flame,
  TrendingUp,
  Star,
  Eye,
  CheckCircle2,
  Compass,
  Globe,
} from 'lucide-react';
import { api, type MediaItem, type SearchDiscoveryData, type TrendingCommunityItem, type SceneSearchResult } from '../lib/api';
import { useThemeLanguage } from '../context/ThemeLanguageContext';

export interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMedia: (id: string) => void;
  onPlayScene?: (mediaItemId: string, timestamp: number) => void;
}

export function SearchOverlay({ isOpen, onClose, onSelectMedia, onPlayScene }: SearchOverlayProps) {
  const { language, t, isRtl } = useThemeLanguage();
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'discover' | 'inLibrary' | 'scenes'>('all');
  const [inLibrary, setInLibrary] = useState<MediaItem[]>([]);
  const [discover, setDiscover] = useState<MediaItem[]>([]);
  const [sceneResults, setSceneResults] = useState<SceneSearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  // Discovery / Recommendation Algorithm state
  const [discovery, setDiscovery] = useState<SearchDiscoveryData | null>(null);
  const [loadingDiscovery, setLoadingDiscovery] = useState(false);
  const searchLogTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch discovery & trending data on open
  useEffect(() => {
    if (isOpen && !discovery) {
      setLoadingDiscovery(true);
      api
        .getSearchDiscovery()
        .then((data) => {
          setDiscovery(data);
          setLoadingDiscovery(false);
        })
        .catch(() => setLoadingDiscovery(false));
    }
  }, [isOpen, discovery]);

  // Handle search query with debounce
  useEffect(() => {
    if (!query.trim()) {
      setInLibrary([]);
      setDiscover([]);
      return;
    }

    const timer = setTimeout(() => {
      setLoading(true);
      api
        .search(query)
        .then((res) => {
          setInLibrary(res.inLibrary || []);
          setDiscover(res.discover || []);
          setLoading(false);
        })
        .catch(() => setLoading(false));

      api
        .searchScenes(query)
        .then((scenes) => setSceneResults(scenes))
        .catch(() => {});

      // Asynchronously log the search query to improve recommendations
      if (searchLogTimer.current) clearTimeout(searchLogTimer.current);
      searchLogTimer.current = setTimeout(() => {
        api.logSearchQuery(query);
      }, 1500);
    }, 300);

    return () => {
      clearTimeout(timer);
      if (searchLogTimer.current) clearTimeout(searchLogTimer.current);
    };
  }, [query]);

  // Unified "All" items: Library items first, followed by Discover items
  const allItems = React.useMemo(() => {
    const libraryIds = new Set(
      inLibrary.map((item) => (item.tmdbId ? `tmdb-${item.tmdbId}` : item.id))
    );
    const uniqueDiscover = discover.filter((item) => {
      const key = item.tmdbId ? `tmdb-${item.tmdbId}` : item.id;
      return !libraryIds.has(key);
    });
    return [...inLibrary, ...uniqueDiscover];
  }, [inLibrary, discover]);

  if (!isOpen) return null;

  const currentItems =
    activeTab === 'all'
      ? allItems
      : activeTab === 'inLibrary'
      ? inLibrary
      : discover;
  const isHebrew = language === 'he';

  const handleSelectQuery = (term: string) => {
    setQuery(term);
    api.logSearchQuery(term);
  };

  const handleSelectMediaItem = (item: MediaItem) => {
    if (query.trim()) {
      api.logSearchQuery(query, item.id);
    }
    onSelectMedia(item.id);
    onClose();
  };

  return (
    <div className="fixed top-14 md:top-16 inset-x-0 bottom-0 z-40 bg-[#0F0F0F]/98 backdrop-blur-2xl flex flex-col px-4 pt-5 pb-8 md:px-8 md:pt-6 md:pb-10 overflow-y-auto select-none search-overlay safe-bottom animate-in fade-in slide-in-from-top-3 duration-200">
      {/* Search Header Bar (Sits nicely right below Navbar) */}
      <div className="w-full max-w-4xl mx-auto flex items-center justify-between pb-3 sm:pb-4 border-b border-white/10 gap-3 sm:gap-4">
        <div className="relative flex-1">
          <Search
            className={`absolute ${isRtl ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6 text-accent-primary pointer-events-none`}
          />
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              isHebrew
                ? 'חפש כל סרט או סדרה בעולם (TMDB, שרתים, ספרייה מקומית)...'
                : 'Search any movie or show in the world (TMDB, Streams, Library)...'
            }
            dir={isRtl ? 'rtl' : 'ltr'}
            className={`w-full ${isRtl ? 'pr-12 pl-10 sm:pr-14 sm:pl-10' : 'pl-12 pr-10 sm:pl-14 sm:pr-10'} py-3 sm:py-3.5 bg-neutral-900/90 border border-white/20 rounded-xl text-base sm:text-lg text-white placeholder-neutral-500 focus:outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary shadow-inner transition-all`}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 p-1.5 text-neutral-400 hover:text-white cursor-pointer`}
              aria-label="Clear search"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}
        </div>

        <button
          onClick={onClose}
          className="p-2.5 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-300 hover:rotate-90 cursor-pointer shrink-0"
          aria-label="Close search"
        >
          <X className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>

      {/* When user has typed a query: Show tabs & search results */}
      {query.trim() && (
        <>
          {/* Tabs */}
          <div className="w-full max-w-4xl mx-auto flex items-center gap-2 sm:gap-3 my-3.5 sm:my-5 overflow-x-auto pt-1 pb-2 px-1 -mx-1 scrollbar-hide">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold transition-all cursor-pointer whitespace-nowrap mood-pill ${
                activeTab === 'all'
                  ? 'mood-pill-active z-10'
                  : 'bg-white/10 text-neutral-400 hover:text-white'
              }`}
            >
              {isHebrew ? 'כל התוצאות' : 'All Titles'} ({allItems.length})
            </button>
            <button
              onClick={() => setActiveTab('discover')}
              className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold transition-all cursor-pointer whitespace-nowrap mood-pill ${
                activeTab === 'discover'
                  ? 'mood-pill-active z-10'
                  : 'bg-white/10 text-neutral-400 hover:text-white'
              }`}
            >
              {isHebrew ? 'גלה בעולם' : 'Global Discover'} ({discover.length})
            </button>
            <button
              onClick={() => setActiveTab('inLibrary')}
              className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold transition-all cursor-pointer whitespace-nowrap mood-pill ${
                activeTab === 'inLibrary'
                  ? 'mood-pill-active z-10'
                  : 'bg-white/10 text-neutral-400 hover:text-white'
              }`}
            >
              {t('inMyLibrary')} ({inLibrary.length})
            </button>
            <button
              onClick={() => setActiveTab('scenes')}
              className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 mood-pill ${
                activeTab === 'scenes'
                  ? 'mood-pill-active z-10'
                  : 'bg-white/10 text-neutral-400 hover:text-white'
              }`}
            >
              <span>{isHebrew ? 'חיפוש סצנות' : 'Scene Search'}</span>
              <span>({sceneResults.length})</span>
            </button>
          </div>

          {/* Results Grid */}
          <div className="w-full max-w-4xl mx-auto flex-1">
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-accent-primary" />
              </div>
            )}

            {/* Scene Search Results */}
            {!loading && activeTab === 'scenes' && (
              sceneResults.length === 0 ? (
                <div className="text-center py-16 text-neutral-400">
                  <p>{isHebrew ? 'לא נמצאו סצנות או דיאלוגים תואמים' : 'No matching dialogue scenes found'}</p>
                </div>
              ) : (
                <div className="space-y-3 pb-8">
                  {sceneResults.map((scene, idx) => (
                    <div
                      key={idx}
                      onClick={() => {
                        onClose();
                        if (onPlayScene) {
                          onPlayScene(scene.mediaItemId, scene.timestamp);
                        } else {
                          onSelectMedia(scene.mediaItemId);
                        }
                      }}
                      className="p-4 rounded-2xl bg-neutral-900/90 hover:bg-neutral-800 border border-white/10 hover:border-accent-primary/40 flex items-center justify-between gap-4 transition-all cursor-pointer group"
                    >
                      <div className="flex items-center gap-3.5 min-w-0">
                        <div className="w-16 h-12 rounded-lg bg-neutral-800 overflow-hidden shrink-0 border border-white/10">
                          <img
                            src={scene.backdropPath || scene.posterPath || 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=400'}
                            alt={scene.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-bold text-white truncate">
                              {isHebrew && scene.titleHe ? scene.titleHe : scene.title}
                            </h4>
                            {scene.episodeTitle && (
                              <span className="text-[11px] text-neutral-400 truncate">
                                ({scene.episodeTitle})
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-amber-300/90 italic line-clamp-1 mt-0.5">
                            "{scene.dialogueSnippet}"
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        <span className="px-2.5 py-1 rounded-lg bg-white/10 text-white font-mono text-xs font-bold flex items-center gap-1">
                          <span>⏱</span>
                          <span>{scene.formattedTime}</span>
                        </span>
                        <div className="p-2 rounded-full bg-accent-primary text-white opacity-0 group-hover:opacity-100 transition-opacity shadow-md shadow-accent-glow">
                          <Play className="w-3.5 h-3.5 fill-current" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {!loading && activeTab !== 'scenes' && currentItems.length === 0 && (
              <div className="text-center py-20 text-neutral-500 space-y-2">
                <Film className="w-12 h-12 mx-auto text-neutral-600 mb-2" />
                <p className="text-lg font-semibold">
                  {t('noResultsFor')} "{query}"
                </p>
                <p className="text-sm">{t('searchHint')}</p>
              </div>
            )}

            {!loading && currentItems.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 pb-12">
                {currentItems.map((item) => {
                  const hasHebrew = Boolean(item.titleHe);
                  const englishTitle = item.title || item.originalTitle || '';
                  const hasBothTitles = Boolean(hasHebrew && englishTitle && item.titleHe !== englishTitle);

                  const displayTitle = isHebrew
                    ? item.titleHe || englishTitle
                    : englishTitle || item.titleHe;

                  const displaySubtitle = hasBothTitles
                    ? isHebrew
                      ? englishTitle
                      : item.titleHe
                    : null;

                  const titleDir = isHebrew ? (item.titleHe ? 'rtl' : 'ltr') : 'ltr';
                  const subtitleDir = isHebrew ? 'ltr' : 'rtl';

                  return (
                    <div
                      key={item.id}
                      onClick={() => handleSelectMediaItem(item)}
                      className="group relative bg-neutral-900 border border-white/10 rounded-2xl overflow-hidden cursor-pointer hover:scale-[1.04] transition-all duration-300 shadow-xl hover:shadow-[0_16px_36px_rgba(229,9,20,0.35)] card-sheen media-card-cinematic"
                    >
                      {/* Top Badges */}
                      {item.inLibrary ? (
                        <div className="absolute top-2.5 left-2.5 z-10 pointer-events-none">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-950/90 text-emerald-300 border border-emerald-500/40 backdrop-blur-md shadow flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                            <span>{isHebrew ? 'בספרייה המקומית' : 'In Library'}</span>
                          </span>
                        </div>
                      ) : (
                        <div className="absolute top-2.5 left-2.5 z-10 pointer-events-none">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-sky-950/90 text-sky-300 border border-sky-500/40 backdrop-blur-md shadow flex items-center gap-1">
                            <Globe className="w-3 h-3 text-sky-400" />
                            <span>{isHebrew ? 'גלובלי • צפייה / הורדה' : 'Global • Stream'}</span>
                          </span>
                        </div>
                      )}

                      {item.rating ? (
                        <div className="absolute top-2.5 right-2.5 z-10 pointer-events-none">
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-black/80 text-amber-300 border border-white/10 backdrop-blur-md shadow flex items-center gap-0.5">
                            ★ {(item.rating * 10).toFixed(0)}%
                          </span>
                        </div>
                      ) : null}

                      <div className="aspect-[2/3] w-full overflow-hidden bg-neutral-950 relative">
                        <img
                          src={
                            item.posterPath ||
                            'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=800'
                          }
                          alt={displayTitle}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />

                        {/* Always-visible Bottom Title Bar */}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/75 to-transparent pt-8 pb-3 px-3 pointer-events-none">
                          <p
                            className="text-xs md:text-sm font-bold text-white truncate drop-shadow"
                            dir={titleDir}
                          >
                            {displayTitle}
                          </p>
                          <div className="flex items-center justify-between text-[11px] text-neutral-400 mt-0.5">
                            {item.year ? <span>{item.year}</span> : <span />}
                            <span className="text-[10px] text-neutral-300">
                              {item.inLibrary
                                ? (isHebrew ? 'מוכן לניגון' : 'Local file')
                                : (isHebrew ? 'צפייה ישירה ברשת' : 'Online Stream')}
                            </span>
                          </div>
                        </div>

                        {/* Hover Overlay with Details */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4 z-20">
                          <h4
                            className="text-sm font-bold text-white leading-snug drop-shadow"
                            dir={titleDir}
                          >
                            {displayTitle}
                          </h4>
                          {displaySubtitle && (
                            <span
                              className="text-[11px] text-neutral-300 block mt-0.5 drop-shadow-sm font-medium"
                              dir={subtitleDir}
                            >
                              {displaySubtitle}
                            </span>
                          )}
                          {item.year && (
                            <span className="text-xs text-neutral-400 mt-0.5">{item.year}</span>
                          )}

                          {item.inLibrary ? (
                            <span className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400 bg-emerald-950/70 px-2 py-1 rounded-lg border border-emerald-500/30 w-fit">
                              <Play className="w-3.5 h-3.5 fill-current" /> {t('inLibrary')}
                            </span>
                          ) : (
                            <span className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-bold text-sky-300 bg-sky-950/70 px-2 py-1 rounded-lg border border-sky-500/30 w-fit">
                              <Globe className="w-3.5 h-3.5" /> {isHebrew ? 'צפה ישירות / הורד' : 'Stream / Download'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* When query is EMPTY: Show rich Trending Searches & Smart Community Recommendations */}
      {!query.trim() && (
        <div className="w-full max-w-4xl mx-auto flex-1 py-4 sm:py-6 space-y-8 animate-in fade-in duration-300">
          {loadingDiscovery && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#E50914]" />
              <p className="text-xs text-neutral-400">
                {isHebrew ? 'מנתח העדפות וצפיות בקהילה...' : 'Calculating community trends...'}
              </p>
            </div>
          )}

          {!loadingDiscovery && discovery && (
            <>
              {/* SECTION 1: Trending Searches Chips */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-white">
                  <div className="p-1.5 rounded-lg bg-orange-500/20 text-orange-400 border border-orange-500/30">
                    <Flame className="w-4 h-4 fill-orange-500/50" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold tracking-wide">
                    {isHebrew ? 'חיפושים חמים בקהילה' : 'Trending Community Searches'}
                  </h3>
                  <span className="text-[10px] sm:text-xs text-neutral-400">
                    {isHebrew ? '(הנפוצים ביותר על ידי משתמשים)' : '(Most popular searches)'}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 sm:gap-2.5 pt-1">
                  {discovery.trendingSearches.map((term, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSelectQuery(term)}
                      className="group flex items-center gap-1.5 px-3.5 py-1.5 sm:px-4 sm:py-2 rounded-full bg-neutral-900/90 hover:bg-[#E50914]/20 border border-white/10 hover:border-[#E50914]/60 text-neutral-200 hover:text-white text-xs sm:text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95 shadow-sm cursor-pointer"
                    >
                      <TrendingUp className="w-3.5 h-3.5 text-[#E50914] group-hover:rotate-12 transition-transform" />
                      <span>{term}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* SECTION 2: Popular Categories & Genres */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-white">
                  <div className="p-1.5 rounded-lg bg-red-500/20 text-[#E50914] border border-[#E50914]/30">
                    <Compass className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold tracking-wide">
                    {isHebrew ? 'ז\'אנרים וקטגוריות מובילות' : 'Popular Categories & Moods'}
                  </h3>
                </div>

                <div className="flex flex-wrap gap-2 sm:gap-2.5">
                  {discovery.popularCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleSelectQuery(isHebrew ? cat.nameHe : cat.nameEn)}
                      className="flex items-center gap-2 px-3.5 py-1.5 sm:px-4 sm:py-2 rounded-xl bg-neutral-900 border border-white/10 hover:border-white/30 text-white text-xs sm:text-sm font-semibold transition-all hover:bg-neutral-800 active:scale-95 cursor-pointer"
                    >
                      <span className="text-base">{cat.emoji}</span>
                      <span>{isHebrew ? cat.nameHe : cat.nameEn}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* SECTION 3: Top Trending on Server (Community Smart Algorithm) */}
              <div className="space-y-3.5 pt-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-white">
                    <div className="p-1.5 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30">
                      <Sparkles className="w-4 h-4 fill-amber-400/40" />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-bold tracking-wide flex items-center gap-2">
                        <span>{isHebrew ? 'הכי נצפים ב-Homeflix' : 'Most Watched on Homeflix'}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E50914]/20 border border-[#E50914]/40 text-[#E50914] font-black uppercase tracking-wider">
                          {isHebrew ? 'אלגוריתם קהילה' : 'Smart Algorithm'}
                        </span>
                      </h3>
                      <p className="text-[11px] sm:text-xs text-neutral-400 mt-0.5">
                        {isHebrew
                          ? 'מדורג לפי כמות צפיות, אחוזי סיום, מועדפים והעדפות משתמשים בשרת'
                          : 'Ranked by view counts, completion rates, favorites, and community watch velocity'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3.5 sm:gap-4">
                  {discovery.trendingCommunity.map((item: TrendingCommunityItem) => {
                    const hasHebrew = Boolean(item.titleHe);
                    const englishTitle = item.title || item.originalTitle || '';
                    const hasBothTitles = Boolean(hasHebrew && englishTitle && item.titleHe !== englishTitle);

                    const displayTitle = isHebrew
                      ? item.titleHe || englishTitle
                      : englishTitle || item.titleHe;

                    const displaySubtitle = hasBothTitles
                      ? isHebrew
                        ? englishTitle
                        : item.titleHe
                      : null;

                    const titleDir = isHebrew ? (item.titleHe ? 'rtl' : 'ltr') : 'ltr';
                    const subtitleDir = isHebrew ? 'ltr' : 'rtl';

                    // Dynamic rank style
                    let rankBg = 'bg-neutral-900/90 text-neutral-300 border-white/20';
                    if (item.communityRank === 1) {
                      rankBg = 'bg-gradient-to-r from-amber-500 to-yellow-400 text-black font-black border-amber-300 shadow-md shadow-amber-500/30';
                    } else if (item.communityRank === 2) {
                      rankBg = 'bg-gradient-to-r from-slate-200 to-gray-400 text-black font-black border-slate-200 shadow-md shadow-slate-300/30';
                    } else if (item.communityRank === 3) {
                      rankBg = 'bg-gradient-to-r from-amber-700 to-amber-900 text-amber-100 font-black border-amber-600';
                    }

                    return (
                      <div
                        key={item.id}
                        onClick={() => handleSelectMediaItem(item)}
                        className="group relative bg-neutral-900 border border-white/10 hover:border-[#E50914]/50 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.03] transition-all duration-300 shadow-lg hover:shadow-[0_12px_32px_rgba(229,9,20,0.3)] flex flex-col"
                      >
                        {/* Poster with rank badge */}
                        <div className="aspect-[2/3] w-full overflow-hidden bg-neutral-950 relative">
                          <img
                            src={
                              item.posterPath ||
                              'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=800'
                            }
                            alt={displayTitle}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />

                          {/* Top Rank Badge */}
                          <div className={`absolute top-2 ${isRtl ? 'right-2' : 'left-2'} z-10 px-2 py-0.5 rounded-md border text-xs font-black shadow-lg flex items-center gap-1 ${rankBg}`}>
                            <span>#{item.communityRank}</span>
                          </div>

                          {/* Trending reason badge */}
                          {item.trendingBadge && (
                            <div className={`absolute bottom-2 ${isRtl ? 'right-2' : 'left-2'} z-10 px-2 py-0.5 rounded-md bg-black/80 backdrop-blur-md border border-white/20 text-[10px] font-bold text-white shadow`}>
                              {item.trendingBadge}
                            </div>
                          )}
                        </div>

                        {/* Card Info */}
                        <div className="p-3 flex-1 flex flex-col justify-between bg-neutral-900/90 gap-1.5">
                          <div>
                            <h4
                              className="text-xs sm:text-sm font-bold text-white line-clamp-1 group-hover:text-[#E50914] transition-colors"
                              dir={titleDir}
                            >
                              {displayTitle}
                            </h4>
                            {displaySubtitle && (
                              <p
                                className="text-[10px] text-neutral-400 line-clamp-1 mt-0.5"
                                dir={subtitleDir}
                              >
                                {displaySubtitle}
                              </p>
                            )}
                          </div>

                          {/* Metrics Footer */}
                          <div className="flex items-center justify-between pt-1 border-t border-white/5 text-[10px] sm:text-[11px] text-neutral-400">
                            {item.watchersCount > 0 ? (
                              <span className="flex items-center gap-1 text-amber-300 font-semibold">
                                <Eye className="w-3 h-3" />
                                <span>{item.watchersCount} {isHebrew ? 'צופים' : 'views'}</span>
                              </span>
                            ) : item.rating ? (
                              <span className="flex items-center gap-1 text-amber-400 font-semibold">
                                <Star className="w-3 h-3 fill-amber-400" />
                                <span>{item.rating.toFixed(1)}</span>
                              </span>
                            ) : (
                              <span>{item.year || ''}</span>
                            )}

                            {item.inLibrary ? (
                              <span className="text-emerald-400 font-bold flex items-center gap-0.5">
                                <CheckCircle2 className="w-3 h-3" />
                                <span>{isHebrew ? 'בספרייה' : 'Library'}</span>
                              </span>
                            ) : item.completionRate > 0 ? (
                              <span className="text-neutral-300 font-medium">
                                {item.completionRate}% {isHebrew ? 'סיימו' : 'comp.'}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* SECTION 4: Available in Local Library */}
              {discovery.inLibraryTrending.length > 0 && (
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-2 text-white">
                    <div className="p-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                      <Play className="w-4 h-4 fill-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-bold tracking-wide">
                        {isHebrew ? 'זמין לצפייה מיידית בספריית השרת' : 'Ready to Stream from Local Library'}
                      </h3>
                      <p className="text-[11px] sm:text-xs text-neutral-400">
                        {isHebrew ? 'מאוחסן בדיסק השרת לצפייה ישירה ללא המתנה' : 'Stored locally for instant playback with zero buffer'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3.5 sm:gap-4 pb-8">
                    {discovery.inLibraryTrending.map((item) => {
                      const displayTitle = isHebrew
                        ? item.titleHe || item.title
                        : item.title || item.titleHe;

                      return (
                        <div
                          key={item.id}
                          onClick={() => handleSelectMediaItem(item)}
                          className="group relative bg-neutral-900 border border-emerald-500/20 hover:border-emerald-500/60 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.03] transition-all duration-300 shadow-lg"
                        >
                          <div className="aspect-[16/9] w-full overflow-hidden bg-neutral-950 relative">
                            <img
                              src={
                                item.backdropPath ||
                                item.posterPath ||
                                'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=800'
                              }
                              alt={displayTitle}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                              <div className="w-10 h-10 rounded-full bg-emerald-500 text-black flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                <Play className="w-5 h-5 fill-black translate-x-0.5" />
                              </div>
                            </div>
                          </div>
                          <div className="p-2.5">
                            <h4 className="text-xs sm:text-sm font-bold text-white line-clamp-1 group-hover:text-emerald-400 transition-colors">
                              {displayTitle}
                            </h4>
                            <div className="flex items-center justify-between text-[10px] text-neutral-400 mt-1">
                              <span>{item.year || ''}</span>
                              <span className="text-emerald-400 font-semibold">{isHebrew ? 'איכות מקסימלית' : 'Direct Play'}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
